// Geometria das formas especiais em polegadas (origem canto inferior esquerdo, y para cima).
// outline: ops ['M',x,y] | ['L',x,y] | ['C',x1,y1,x2,y2,x,y] | ['Z']; dims: cotas {x1,y1,x2,y2,side,value}
import { SHAPE_FIELDS, SHAPE_TYPE_IDS } from './openingShapes';

const K = 0.5523; // aproximação cúbica de 1/4 de elipse

const quarter = (cx, cy, rx, ry, from) => {
  // from: 'E' (0°→90°), 'N' (90°→180°), 'W' (180°→270°), 'S' (270°→360°)
  switch (from) {
    case 'E': return ['C', cx + rx, cy + K * ry, cx + K * rx, cy + ry, cx, cy + ry];
    case 'N': return ['C', cx - K * rx, cy + ry, cx - rx, cy + K * ry, cx - rx, cy];
    case 'W': return ['C', cx - rx, cy - K * ry, cx - K * rx, cy - ry, cx, cy - ry];
    default: return ['C', cx + K * rx, cy - ry, cx + rx, cy - K * ry, cx + rx, cy];
  }
};

export const isShapeType = (type) => SHAPE_TYPE_IDS.includes(type);
export const measuresOf = (o) => ({ ...(SHAPE_FIELDS[o.type] ? (o.measures || {}) : { width: o.width, height: o.height }), ...(o.side ? { side: o.side } : {}) });

const MIRRORED = ['HALF_EYEBROW', 'HALF_EYEBROW_LEG', 'QUARTER_ROUND', 'QUARTER_ARCH_LEG']; // desenho padrão = lado baixo à direita; side='left' espelha
const FLIP = { left: 'right', right: 'left' };
const mirror = (g) => {
  const W = g.w;
  return {
    ...g,
    outline: g.outline.map((o) => (o[0] === 'C' ? ['C', W - o[1], o[2], W - o[3], o[4], W - o[5], o[6]] : o[0] === 'M' || o[0] === 'L' ? [o[0], W - o[1], o[2]] : o)),
    dims: g.dims.map((d) => (['bottom', 'top', 'mid'].includes(d.side) ? { ...d, x1: W - d.x2, x2: W - d.x1 } : { ...d, x1: W - d.x1, x2: W - d.x2, side: FLIP[d.side] || d.side })),
  };
};

export const shapeGeometry = (type, m = {}) => {
  const g = baseGeometry(type, m);
  return MIRRORED.includes(type) && m.side === 'left' ? mirror(g) : g;
};

const baseGeometry = (type, m = {}) => {
  const n = (k) => Math.max(0, Number(m[k]) || 0);
  const W = n('width'), H = n('height');
  switch (type) {
    case 'EYEBROW': case 'EYEBROW_LEG': {
      const L = n('leg_left'), R = n('leg_right'), M = n('mid_height') || H; // Eyebrow sem pernas: largura × altura
      const qy = 2 * M - (L + R) / 2; // controle da quadrática p/ o pico ficar em M
      const c1 = [W + (2 / 3) * (W / 2 - W), R + (2 / 3) * (qy - R)], c2 = [0 + (2 / 3) * (W / 2 - 0), L + (2 / 3) * (qy - L)];
      return { w: W, h: Math.max(L, R, M), outline: [['M', 0, 0], ['L', W, 0], ['L', W, R], ['C', c1[0], c1[1], c2[0], c2[1], 0, L], ['Z']],
        dims: [{ x1: 0, y1: 0, x2: W, y2: 0, side: 'bottom', value: W }, { x1: 0, y1: 0, x2: 0, y2: L, side: 'left', value: L }, { x1: W, y1: 0, x2: W, y2: R, side: 'right', value: R }, { x1: W / 2, y1: 0, x2: W / 2, y2: M, side: 'mid', value: M }] };
    }
    case 'HALF_EYEBROW': case 'HALF_EYEBROW_LEG': { // lado reto à esquerda (altura total), perna curta à direita e curva eyebrow (quadrática, pico no lado alto)
      const L = n('leg_height');
      return { w: W, h: H, outline: [['M', 0, 0], ['L', W, 0], ['L', W, L], ['C', (2 * W) / 3, L + (2 * (H - L)) / 3, W / 3, H, 0, H], ['Z']],
        dims: [{ x1: 0, y1: 0, x2: W, y2: 0, side: 'bottom', value: W }, { x1: 0, y1: 0, x2: 0, y2: H, side: 'left', value: H }, { x1: W, y1: 0, x2: W, y2: L, side: 'right', value: L }] };
    }
    case 'OVAL': {
      const cx = W / 2, cy = H / 2;
      return { w: W, h: H, outline: [['M', W, cy], quarter(cx, cy, cx, cy, 'E'), quarter(cx, cy, cx, cy, 'N'), quarter(cx, cy, cx, cy, 'W'), quarter(cx, cy, cx, cy, 'S'), ['Z']],
        dims: [{ x1: 0, y1: 0, x2: W, y2: 0, side: 'bottom', value: W }, { x1: 0, y1: 0, x2: 0, y2: H, side: 'left', value: H }] };
    }
    case 'TRAPEZOID': {
      const BW = n('bottom_width'), TW = n('top_width'), L = n('leg_left'), R = n('leg_right');
      const b = Math.max(0, (TW - BW) / 2), t = Math.max(0, (BW - TW) / 2), w = Math.max(BW, TW);
      return { w, h: Math.max(L, R), outline: [['M', b, 0], ['L', b + BW, 0], ['L', t + TW, R], ['L', t, L], ['Z']],
        dims: [{ x1: b, y1: 0, x2: b + BW, y2: 0, side: 'bottom', value: BW }, { x1: t, y1: Math.max(L, R), x2: t + TW, y2: Math.max(L, R), side: 'top', value: TW }, { x1: 0, y1: 0, x2: 0, y2: L, side: 'left', value: L }, { x1: w, y1: 0, x2: w, y2: R, side: 'right', value: R }] };
    }
    case 'CIRCLE': {
      const D = n('diameter'), r = D / 2;
      return { w: D, h: D, outline: [['M', D, r], quarter(r, r, r, r, 'E'), quarter(r, r, r, r, 'N'), quarter(r, r, r, r, 'W'), quarter(r, r, r, r, 'S'), ['Z']],
        dims: [{ x1: 0, y1: r, x2: D, y2: r, side: 'mid', value: D }] };
    }
    case 'HALF_ROUND':
      return { w: W, h: H, outline: [['M', 0, 0], ['L', W, 0], quarter(W / 2, 0, W / 2, H, 'E'), quarter(W / 2, 0, W / 2, H, 'N'), ['Z']],
        dims: [{ x1: 0, y1: 0, x2: W, y2: 0, side: 'bottom', value: W }, { x1: 0, y1: 0, x2: 0, y2: H, side: 'left', value: H }] };
    case 'QUARTER_ROUND':
      return { w: W, h: H, outline: [['M', 0, 0], ['L', W, 0], quarter(0, 0, W, H, 'E'), ['Z']],
        dims: [{ x1: 0, y1: 0, x2: W, y2: 0, side: 'bottom', value: W }, { x1: 0, y1: 0, x2: 0, y2: H, side: 'left', value: H }] };
    case 'OCTAGON': {
      const c = Math.min(W, H) * 0.29;
      return { w: W, h: H, outline: [['M', c, 0], ['L', W - c, 0], ['L', W, c], ['L', W, H - c], ['L', W - c, H], ['L', c, H], ['L', 0, H - c], ['L', 0, c], ['Z']],
        dims: [{ x1: 0, y1: 0, x2: W, y2: 0, side: 'bottom', value: W }, { x1: 0, y1: 0, x2: 0, y2: H, side: 'left', value: H }] };
    }
    case 'SPRINGLINE': {
      const L = n('leg_height'), ry = Math.max(0, H - L);
      return { w: W, h: H, outline: [['M', 0, 0], ['L', W, 0], ['L', W, L], quarter(W / 2, L, W / 2, ry, 'E'), quarter(W / 2, L, W / 2, ry, 'N'), ['Z']],
        dims: [{ x1: 0, y1: 0, x2: W, y2: 0, side: 'bottom', value: W }, { x1: 0, y1: 0, x2: 0, y2: L, side: 'left', value: L }, { x1: W / 2, y1: 0, x2: W / 2, y2: H, side: 'mid', value: H }] };
    }
    case 'QUARTER_ARCH_LEG': {
      const L = n('leg_height');
      return { w: W, h: H, outline: [['M', 0, 0], ['L', W, 0], ['L', W, L], quarter(0, L, W, Math.max(0, H - L), 'E'), ['Z']],
        dims: [{ x1: 0, y1: 0, x2: W, y2: 0, side: 'bottom', value: W }, { x1: 0, y1: 0, x2: 0, y2: H, side: 'left', value: H }, { x1: W, y1: 0, x2: W, y2: L, side: 'right', value: L }] };
    }
    case 'TRIANGLE':
      return { w: W, h: H, outline: [['M', 0, 0], ['L', W, 0], ['L', W / 2, H], ['Z']],
        dims: [{ x1: 0, y1: 0, x2: W, y2: 0, side: 'bottom', value: W }, { x1: W / 2, y1: 0, x2: W / 2, y2: H, side: 'mid', value: H }] };
    default:
      return { w: W, h: H, outline: [['M', 0, 0], ['L', W, 0], ['L', W, H], ['L', 0, H], ['Z']],
        dims: [{ x1: 0, y1: 0, x2: W, y2: 0, side: 'bottom', value: W }, { x1: 0, y1: 0, x2: 0, y2: H, side: 'left', value: H }] };
  }
};
